<template>
  <div class="naming-panel">

    <!-- ==================== 移动端独立模板 ==================== -->
    <div v-if="isMobile" class="mobile-naming-view">
      <p class="mobile-motto">✍️ 生命的第一份祝福</p>

      <!-- 加载用户姓名提示 -->
      <div v-if="isOwnName" class="mobile-banner">
        <span>👤 已加载您的姓名</span>
        <el-button size="small" text @click="clearNameInput">查询其他</el-button>
      </div>

      <!-- 移动端：名字横向展示 -->
      <div v-if="displayChars.length" class="mobile-name-display">
        <span v-for="(char, i) in displayChars" :key="i" class="mobile-name-char" :class="{ 'char-surname': i === 0 }">
          {{ char }}
        </span>
      </div>

      <!-- 移动端：紧凑输入卡片 -->
      <div class="mobile-input-card">
        <!-- 第一行：姓氏+性别+名字类型 -->
        <div class="mobile-input-top-row">
          <div class="mobile-field">
            <label class="mobile-label">姓氏</label>
            <el-input
              v-model="charInput.surname"
              maxlength="1"
              placeholder="姓"
              class="mobile-input"
              @input="onCharInput('surname')"
            />
          </div>
          <div class="mobile-field mobile-field--type">
            <el-radio-group v-model="strokeData.nameType" @change="resetNameStrokes" size="small">
              <el-radio-button value="double">双字</el-radio-button>
              <el-radio-button value="triple">三字</el-radio-button>
            </el-radio-group>
          </div>
        </div>

        <!-- 第二行：字1输入 -->
        <div class="mobile-stroke-step">
          <div class="mobile-step-header">
            <span class="mobile-step-num">1</span>
            <span class="mobile-step-label">名字第一字</span>
            <span v-if="charInput.firstName && charStrokes.firstName" class="mobile-stroke-info">
              {{ charStrokes.firstName }}画
            </span>
            <span v-else-if="charInput.firstName && !charStrokes.firstName" class="mobile-stroke-warn">
              未收录
            </span>
          </div>
          <div class="mobile-step-row">
            <el-input
              v-model="charInput.firstName"
              maxlength="1"
              placeholder="输入第一字"
              class="mobile-input"
              @input="onCharInput('firstName')"
            />
            <el-input-number
              v-model="strokeData.firstName"
              :min="1"
              :max="30"
              controls-position="right"
              size="small"
              class="mobile-num-input"
            />
            <span class="mobile-input-unit">画</span>
          </div>
        </div>

        <!-- 第三行：字2输入（三字名时显示） -->
        <div v-if="strokeData.nameType === 'triple'" class="mobile-stroke-step">
          <div class="mobile-step-header">
            <span class="mobile-step-num">2</span>
            <span class="mobile-step-label">名字第二字</span>
            <span v-if="charInput.secondName && charStrokes.secondName" class="mobile-stroke-info">
              {{ charStrokes.secondName }}画
            </span>
            <span v-else-if="charInput.secondName && !charStrokes.secondName" class="mobile-stroke-warn">
              未收录
            </span>
          </div>
          <div class="mobile-step-row">
            <el-input
              v-model="charInput.secondName"
              maxlength="1"
              placeholder="输入第二字"
              class="mobile-input"
              @input="onCharInput('secondName')"
            />
            <el-input-number
              v-model="strokeData.secondName"
              :min="1"
              :max="30"
              controls-position="right"
              size="small"
              class="mobile-num-input"
            />
            <span class="mobile-input-unit">画</span>
          </div>
        </div>

        <div v-if="hasCharWarning" class="mobile-char-hint">
          ⚠️ 部分汉字未收录康熙笔画，请使用右侧手动输入笔画数
        </div>
      </div>

      <!-- 移动端：五格纵向卡片堆叠 -->
      <div v-if="fiveGridsResult" class="mobile-grids-section">
        <h4 class="mobile-section-title">📊 五格数理分析</h4>
        <div class="mobile-grids-list">
          <div class="mobile-grid-card" :class="getGridClass('tianGe')">
            <div class="mobile-grid-label">天格</div>
            <div class="mobile-grid-value">{{ fiveGridsResult.tianGe }}</div>
            <div class="mobile-grid-element">{{ getElement(fiveGridsResult.tianGe) }}行</div>
            <div class="mobile-grid-fortune" :class="getFortuneClass(getGridFortune(fiveGridsResult.tianGe))">
              {{ getGridFortune(fiveGridsResult.tianGe) }}
            </div>
          </div>
          <div class="mobile-grid-card" :class="getGridClass('renGe')">
            <div class="mobile-grid-label">人格</div>
            <div class="mobile-grid-value">{{ fiveGridsResult.renGe }}</div>
            <div class="mobile-grid-element">{{ getElement(fiveGridsResult.renGe) }}行</div>
            <div class="mobile-grid-fortune" :class="getFortuneClass(getGridFortune(fiveGridsResult.renGe))">
              {{ getGridFortune(fiveGridsResult.renGe) }}
            </div>
          </div>
          <div class="mobile-grid-card" :class="getGridClass('diGe')">
            <div class="mobile-grid-label">地格</div>
            <div class="mobile-grid-value">{{ fiveGridsResult.diGe }}</div>
            <div class="mobile-grid-element">{{ getElement(fiveGridsResult.diGe) }}行</div>
            <div class="mobile-grid-fortune" :class="getFortuneClass(getGridFortune(fiveGridsResult.diGe))">
              {{ getGridFortune(fiveGridsResult.diGe) }}
            </div>
          </div>
          <div class="mobile-grid-card" :class="getGridClass('zongGe')">
            <div class="mobile-grid-label">总格</div>
            <div class="mobile-grid-value">{{ fiveGridsResult.zongGe }}</div>
            <div class="mobile-grid-element">{{ getElement(fiveGridsResult.zongGe) }}行</div>
            <div class="mobile-grid-fortune" :class="getFortuneClass(getGridFortune(fiveGridsResult.zongGe))">
              {{ getGridFortune(fiveGridsResult.zongGe) }}
            </div>
          </div>
          <div class="mobile-grid-card" :class="getGridClass('waiGe')">
            <div class="mobile-grid-label">外格</div>
            <div class="mobile-grid-value">{{ fiveGridsResult.waiGe }}</div>
            <div class="mobile-grid-element">{{ getElement(fiveGridsResult.waiGe) }}行</div>
            <div class="mobile-grid-fortune" :class="getFortuneClass(getGridFortune(fiveGridsResult.waiGe))">
              {{ getGridFortune(fiveGridsResult.waiGe) }}
            </div>
          </div>
        </div>
      </div>

      <!-- 移动端：三才配置 -->
      <div v-if="threeTalentsResult" class="mobile-talents-section">
        <h4 class="mobile-section-title">🔮 三才配置</h4>
        <div class="mobile-talents-card">
          <div class="mobile-talents-header">
            <div class="mobile-talents-elements">
              <span class="mobile-talent-chip">{{ getElement(fiveGridsResult.tianGe) }}</span>
              <span class="mobile-talent-chip">{{ getElement(fiveGridsResult.renGe) }}</span>
              <span class="mobile-talent-chip">{{ getElement(fiveGridsResult.diGe) }}</span>
            </div>
            <span class="mobile-talents-type" :class="getTalentsClass(threeTalentsResult.type)">
              {{ threeTalentsResult.type }}
            </span>
          </div>
          <p class="mobile-talents-desc">{{ threeTalentsResult.description }}</p>
        </div>
      </div>

      <!-- 综合评分 + AI 取名报告（移动端） -->
      <NamingAIReport v-if="aiReportData" v-bind="aiReportData" />
    </div>

    <!-- ==================== PC端模板 ==================== -->
    <div v-else class="naming-view">
      <p class="page-motto">✍️ 生命的第一份祝福，是心底永不熄灭的星光。</p>

      <!-- 加载用户姓名提示 -->
      <div v-if="isOwnName" class="own-name-banner">
        <span>👤 已加载您的姓名，可直接查看三才五格分析</span>
        <el-button size="small" text @click="clearNameInput">查询其他姓名</el-button>
      </div>

      <!-- 三才五格计算器 -->
      <MysticalCard size="default" hoverable class="calculator-section">
        <template #header>
          <div class="card-header">
            <span>✍️ 三才五格计算器</span>
            <div class="header-tips">
              <span>按康熙字典繁体笔画数输入</span>
              <div v-if="fiveGridsResult" class="name-type-badge">{{ strokeData.nameType === 'double' ? '双字名' : '三字名' }}</div>
            </div>
          </div>
        </template>
        
        <!-- 笔画输入区域 -->
        <div class="stroke-calculator">
          <!-- 康熙字典提示 -->
          <div class="kangxi-tip">
            💡 笔画数采用康熙字典标准（繁体字笔画），可能与简体笔画不同。如：氵按4画、艹按6画、辶按7画。
          </div>

          <!-- 模式切换 -->
          <div class="input-mode-switch">
            <el-radio-group v-model="inputMode" size="small">
              <el-radio-button value="char">汉字输入模式</el-radio-button>
              <el-radio-button value="manual">手动输入模式</el-radio-button>
            </el-radio-group>
          </div>

          <!-- 姓名类型切换 -->
          <div class="name-type-row">
            <el-radio-group v-model="strokeData.nameType" @change="resetNameStrokes" size="small">
              <el-radio-button value="double">双字名</el-radio-button>
              <el-radio-button value="triple">三字名</el-radio-button>
            </el-radio-group>
          </div>

          <!-- 汉字输入模式 -->
          <div v-if="inputMode === 'char'" class="char-input-mode">
            <div class="char-input-row">
              <!-- 姓氏 -->
              <div class="char-input-item">
                <label class="char-label">姓氏</label>
                <div class="char-input-wrapper">
                  <el-input 
                    v-model="charInput.surname" 
                    maxlength="1"
                    placeholder="输入姓氏"
                    class="char-input"
                    @input="onCharInput('surname')"
                  />
                  <div v-if="charInput.surname" class="stroke-tag" :class="{ 'tag-warning': !charStrokes.surname }">
                    <template v-if="charStrokes.surname">
                      康熙笔画：{{ charStrokes.surname }}画
                    </template>
                    <template v-else>
                      ⚠️ 未收录
                    </template>
                  </div>
                </div>
              </div>

              <!-- 名字第一字 -->
              <div class="char-input-item">
                <label class="char-label">名字第一字</label>
                <div class="char-input-wrapper">
                  <el-input 
                    v-model="charInput.firstName" 
                    maxlength="1"
                    placeholder="输入第一字"
                    class="char-input"
                    @input="onCharInput('firstName')"
                  />
                  <div v-if="charInput.firstName" class="stroke-tag" :class="{ 'tag-warning': !charStrokes.firstName }">
                    <template v-if="charStrokes.firstName">
                      康熙笔画：{{ charStrokes.firstName }}画
                    </template>
                    <template v-else>
                      ⚠️ 未收录
                    </template>
                  </div>
                </div>
              </div>

              <!-- 名字第二字 -->
              <div v-if="strokeData.nameType === 'triple'" class="char-input-item">
                <label class="char-label">名字第二字</label>
                <div class="char-input-wrapper">
                  <el-input 
                    v-model="charInput.secondName" 
                    maxlength="1"
                    placeholder="输入第二字"
                    class="char-input"
                    @input="onCharInput('secondName')"
                  />
                  <div v-if="charInput.secondName" class="stroke-tag" :class="{ 'tag-warning': !charStrokes.secondName }">
                    <template v-if="charStrokes.secondName">
                      康熙笔画：{{ charStrokes.secondName }}画
                    </template>
                    <template v-else>
                      ⚠️ 未收录
                    </template>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="hasCharWarning" class="char-input-hint">
              ⚠️ 部分汉字未收录康熙笔画，请切换到手动输入模式
            </div>
          </div>

          <!-- 手动输入模式 -->
          <div v-else class="manual-input-mode">
            <div class="manual-tip">
              请使用康熙字典笔画数（繁体字笔画可能与简体不同）
            </div>
            <div class="calculator-row">
              <!-- 第一列：姓氏 -->
              <div class="calculator-column">
                <div class="column-header">
                  <h4>姓氏笔画</h4>
                </div>
                <div class="stroke-input-group">
                  <div class="input-with-label">
                    <el-input-number 
                      v-model="strokeData.surnameStrokes" 
                      :min="1" 
                      :max="30"
                      placeholder="姓氏笔画数"
                      controls-position="right"
                    />
                    <span class="input-label">画</span>
                  </div>
                </div>
              </div>
              
              <!-- 第二列：名字第一字 -->
              <div class="calculator-column">
                <div class="column-header">
                  <h4>名字笔画</h4>
                </div>
                <div class="stroke-input-group">
                  <div class="input-with-label">
                    <el-input-number 
                      v-model="strokeData.firstName" 
                      :min="1" 
                      :max="30"
                      placeholder="第一字笔画"
                      controls-position="right"
                    />
                    <span class="input-label">画</span>
                  </div>
                  <!-- 快捷按钮 -->
                  <div class="quick-buttons">
                    <el-button 
                      size="small" 
                      @click="adjustFirstName(-10)"
                      :disabled="strokeData.firstName <= 10"
                      class="adjust-button"
                    >
                      -10
                    </el-button>
                    <el-button 
                      size="small" 
                      @click="adjustFirstName(10)"
                      :disabled="strokeData.firstName >= 21"
                      class="adjust-button"
                    >
                      +10
                    </el-button>
                  </div>
                </div>
              </div>
              
              <!-- 第三列：名字第二字 -->
              <div v-if="strokeData.nameType === 'triple'" class="calculator-column">
                <div class="column-header">
                  <h4>第二字笔画</h4>
                </div>
                <div class="stroke-input-group">
                  <div class="input-with-label">
                    <el-input-number 
                      v-model="strokeData.secondName" 
                      :min="1" 
                      :max="30"
                      placeholder="第二字笔画"
                      controls-position="right"
                    />
                    <span class="input-label">画</span>
                  </div>
                  <!-- 快捷按钮 -->
                  <div class="quick-buttons">
                    <el-button 
                      size="small" 
                      @click="adjustSecondName(-10)"
                      :disabled="strokeData.secondName <= 10"
                      class="adjust-button"
                    >
                      -10
                    </el-button>
                    <el-button 
                      size="small" 
                      @click="adjustSecondName(10)"
                      :disabled="strokeData.secondName >= 21"
                      class="adjust-button"
                    >
                      +10
                    </el-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 五格结果显示 -->
        <div v-if="fiveGridsResult" class="grids-result-section">
          <div class="section-divider"></div>
          
          <div class="grids-display">
            <h4 class="section-title">📊 五格数理分析</h4>
            <div class="grid-row">
              <div class="grid-item" :class="getGridClass('tianGe')">
                <div class="grid-name">天格</div>
                <div class="grid-value">{{ fiveGridsResult.tianGe }}</div>
                <div class="grid-element">{{ getElement(fiveGridsResult.tianGe) }}</div>
                <div class="grid-fortune">{{ getGridFortune(fiveGridsResult.tianGe) }}</div>
              </div>
              
              <div class="grid-item" :class="getGridClass('renGe')">
                <div class="grid-name">人格</div>
                <div class="grid-value">{{ fiveGridsResult.renGe }}</div>
                <div class="grid-element">{{ getElement(fiveGridsResult.renGe) }}</div>
                <div class="grid-fortune">{{ getGridFortune(fiveGridsResult.renGe) }}</div>
              </div>
              
              <div class="grid-item" :class="getGridClass('diGe')">
                <div class="grid-name">地格</div>
                <div class="grid-value">{{ fiveGridsResult.diGe }}</div>
                <div class="grid-element">{{ getElement(fiveGridsResult.diGe) }}</div>
                <div class="grid-fortune">{{ getGridFortune(fiveGridsResult.diGe) }}</div>
              </div>
              
              <div class="grid-item" :class="getGridClass('zongGe')">
                <div class="grid-name">总格</div>
                <div class="grid-value">{{ fiveGridsResult.zongGe }}</div>
                <div class="grid-element">{{ getElement(fiveGridsResult.zongGe) }}</div>
                <div class="grid-fortune">{{ getGridFortune(fiveGridsResult.zongGe) }}</div>
              </div>
              
              <div class="grid-item" :class="getGridClass('waiGe')">
                <div class="grid-name">外格</div>
                <div class="grid-value">{{ fiveGridsResult.waiGe }}</div>
                <div class="grid-element">{{ getElement(fiveGridsResult.waiGe) }}</div>
                <div class="grid-fortune">{{ getGridFortune(fiveGridsResult.waiGe) }}</div>
              </div>
            </div>
          </div>

          <!-- 三才配置区域重新设计 -->
          <div v-if="threeTalentsResult" class="talents-layout-section">
            <h4 class="section-title">🔮 三才配置分析</h4>
            
            <div class="talents-grid-layout">
              <!-- 左侧：三才配置主卡片 -->
              <div class="grid-card center-card main-talents-card">
                <div class="talents-header">
                  <h5>三才配置</h5>
                  <div class="config-type" :class="getTalentsClass(threeTalentsResult.type)">
                    {{ threeTalentsResult.type }}
                  </div>
                </div>
                
                <div class="talents-combination">
                  <div class="talent-item">
                    <span class="talent-label">天才</span>
                    <span class="talent-element">{{ getElement(fiveGridsResult.tianGe) }}</span>
                  </div>
                  <div class="talent-item">
                    <span class="talent-label">人才</span>
                    <span class="talent-element">{{ getElement(fiveGridsResult.renGe) }}</span>
                  </div>
                  <div class="talent-item">
                    <span class="talent-label">地才</span>
                    <span class="talent-element">{{ getElement(fiveGridsResult.diGe) }}</span>
                  </div>
                </div>
                
                <div class="talents-description">
                  {{ threeTalentsResult.description }}
                </div>
              </div>
              
              <!-- 右侧：总格和外格 -->
              <div class="grid-card right-card additional-grids">
                <!-- 总格 -->
                <div class="number-query-result">
                  <div class="result-header">
                    <span class="number-value">{{ fiveGridsResult.zongGe }}</span>
                    <span class="number-name">总格</span>
                    <span class="number-type" :class="getFortuneClass(getGridFortune(fiveGridsResult.zongGe))">
                      {{ getGridFortune(fiveGridsResult.zongGe) }}
                    </span>
                  </div>
                  <div class="number-meaning">
                    <strong>五行属性：</strong>{{ getElement(fiveGridsResult.zongGe) }}行
                  </div>
                  <div class="number-description">
                    <span class="grid-role">总格代表一生的总运势，影响整体命运走向。</span>{{ getGridMeaning(fiveGridsResult.zongGe) }}
                  </div>
                </div>
                
                <!-- 外格 -->
                <div class="number-query-result">
                  <div class="result-header">
                    <span class="number-value">{{ fiveGridsResult.waiGe }}</span>
                    <span class="number-name">外格</span>
                    <span class="number-type" :class="getFortuneClass(getGridFortune(fiveGridsResult.waiGe))">
                      {{ getGridFortune(fiveGridsResult.waiGe) }}
                    </span>
                  </div>
                  <div class="number-meaning">
                    <strong>五行属性：</strong>{{ getElement(fiveGridsResult.waiGe) }}行
                  </div>
                  <div class="number-description">
                    <span class="grid-role">外格代表社交运势，影响人际关系和外在表现。</span>{{ getGridMeaning(fiveGridsResult.waiGe) }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 综合评分 + AI 取名报告（PC） -->
          <NamingAIReport v-if="aiReportData" v-bind="aiReportData" />
        </div>
      </MysticalCard>

      <!-- 查询工具区域 -->
      <MysticalCard size="default" hoverable class="query-tools-section">
        <template #header>
          <div class="card-header">
            <span>🔍 姓名学查询工具</span>
          </div>
        </template>
        
        <el-tabs v-model="activeQueryTab" class="query-tabs">
          <!-- 三才配置查询 -->
          <el-tab-pane label="三才配置查询" name="threeTalents" class="query-tab-pane">
            <div class="talents-query-section">
              <!-- 天格配置建议 -->
              <div v-if="showTianGeConfigs && tianGeConfigs" class="tiange-suggestions-section">
                <div class="section-header">
                  <h4>🎯 天格{{ tianGeConfigs.tianGe }}({{ tianGeConfigs.tianElement }}行) 起名方案</h4>
                  <p class="section-desc">根据您的天格数理，为您推荐以下三才配置方案</p>
                </div>
                
                <div class="suggestions-display">
                  <div 
                    v-for="(configs, level) in tianGeConfigs.suggestions" 
                    :key="level"
                    class="suggestion-level"
                    :class="getSuggestionLevelClass(level)"
                  >
                    <div class="level-header">
                      <h5>{{ level }}</h5>
                      <span class="count-badge">{{ configs.length }}个</span>
                    </div>
                    
                    <div class="configs-list">
                      <div 
                        v-for="config in configs" 
                        :key="config.config"
                        class="config-suggestion"
                      >
                        <div class="config-header">
                          <span class="config-name">{{ config.config }}</span>
                          <div class="config-elements">
                            <span class="element">{{ config.tianElement }}</span>
                            <span class="element">{{ config.renElement }}</span>
                            <span class="element">{{ config.diElement }}</span>
                          </div>
                        </div>
                        
                        <div class="config-examples">
                          <div 
                            v-for="example in config.examples" 
                            :key="example.combination"
                            class="example-item"
                          >
                            <span class="combination">{{ example.combination }}</span>
                            <span class="description">{{ example.description }}</span>
                          </div>
                        </div>
                        
                        <div class="config-description">
                          {{ config.description }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              

            </div>
          </el-tab-pane>
          
          <!-- 数理吉凶查询 -->
          <el-tab-pane label="数理吉凶查询" name="numbers" class="query-tab-pane">
            <div class="numbers-query-section">
              <div class="query-form">
                <h4>输入数理（1-81）</h4>
                <div class="number-input-group">
                  <el-input-number 
                    v-model="numberQuery" 
                    :min="1" 
                    :max="81"
                    placeholder="请输入数理"
                    controls-position="right"
                    style="width: 150px"
                  />
                  <MysticalButton 
                    @click="queryNumber"
                    :disabled="!numberQuery"
                    class="query-button"
                  >
                    查询
                  </MysticalButton>
                </div>
              </div>
              
              <!-- 数理查询结果 -->
              <div v-if="numberQueryResult" class="number-query-result">
                <div class="result-header">
                  <span class="number-value">{{ numberQuery }}</span>
                  <span class="number-name">{{ numberQueryResult.name }}</span>
                  <span class="number-type" :class="getFortuneClass(numberQueryResult.type)">
                    {{ numberQueryResult.type }}
                  </span>
                </div>
                <div class="number-meaning">
                  <strong>数理含义：</strong>{{ numberQueryResult.meaning }}
                </div>
                <div class="number-description">
                  <strong>详细解释：</strong>{{ numberQueryResult.description }}
                </div>
              </div>
              
              <!-- 数理速查表 -->
              <div class="numbers-quick-reference">
                <h4>数理速查表</h4>
                <div class="numbers-grid">
                  <div 
                    v-for="num in 81" 
                    :key="num"
                    class="number-cell"
                    :class="getNumberCellClass(num)"
                    @click="selectNumber(num)"
                  >
                    {{ num }}
                  </div>
                </div>
                <div class="legend">
                  <span class="legend-item fortune-excellent">大吉</span>
                  <span class="legend-item fortune-good">吉</span>
                  <span class="legend-item fortune-average">半吉</span>
                  <span class="legend-item fortune-bad">凶</span>
                </div>
              </div>
            </div>
          </el-tab-pane>
          
          <!-- 五行凶吉分类 -->
          <el-tab-pane label="五行凶吉分类" name="elementClassification" class="query-tab-pane">
            <div class="element-classification-section">
              <div class="query-form">
                <h4>选择天格五行</h4>
                <div class="element-selector">
                  <el-radio-group v-model="selectedElement" @change="queryElementClassification">
                    <el-radio-button value="木">木</el-radio-button>
                    <el-radio-button value="火">火</el-radio-button>
                    <el-radio-button value="土">土</el-radio-button>
                    <el-radio-button value="金">金</el-radio-button>
                    <el-radio-button value="水">水</el-radio-button>
                  </el-radio-group>
                </div>
              </div>
              
              <!-- 五行统计信息 -->
              <div v-if="elementStatistics" class="element-statistics">
                <h4>{{ selectedElement }}行天格统计</h4>
                <div class="stats-row">
                  <div class="stat-item">
                    <span class="stat-label">总配置数：</span>
                    <span class="stat-value">{{ elementStatistics.总数}}</span>
                  </div>
                  <div class="stat-item">
                    <span class="stat-label">吉利配置：</span>
                    <span class="stat-value good">{{ elementStatistics.大吉 + elementStatistics.中吉 + elementStatistics.吉 + elementStatistics.吉多于凶 }}</span>
                  </div>
                  <div class="stat-item">
                    <span class="stat-label">吉利占比：</span>
                    <span class="stat-value" :class="getPercentageClass(elementStatistics.吉利占比)">{{ elementStatistics.吉利占比 }}</span>
                  </div>
                </div>
              </div>
              
              <!-- 分类展示 -->
              <div v-if="elementClassification" class="element-classification-display">
                <div class="classification-columns">
                  <div 
                    v-for="(configs, level) in elementClassification" 
                    :key="level"
                    class="classification-column"
                    :class="getClassificationColumnClass(level)"
                  >
                    <div class="column-header">
                      <h5>{{ level }}</h5>
                      <span class="count-badge">{{ configs.length }}</span>
                    </div>
                    <div class="config-list">
                      <div 
                        v-for="config in configs" 
                        :key="config.config"
                        class="config-item"
                        @click="viewConfigDetail(config)"
                      >
                        <div class="config-name">{{ config.config }}</div>
                        <div class="config-elements">
                          <span class="element">{{ config.tianElement }}</span>
                          <span class="element">{{ config.renElement }}</span>
                          <span class="element">{{ config.diElement }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- 配置详情弹窗 -->
              <el-dialog 
                v-model="configDetailVisible" 
                :title="selectedConfig?.config + ' 配置详情'"
                width="60%"
                class="config-detail-dialog"
              >
                <div v-if="selectedConfig" class="config-detail">
                  <div class="detail-header">
                    <div class="config-elements-large">
                      <span class="element-large">{{ selectedConfig.tianElement }}</span>
                      <span class="element-large">{{ selectedConfig.renElement }}</span>
                      <span class="element-large">{{ selectedConfig.diElement }}</span>
                    </div>
                    <div class="config-type" :class="getFortuneClass(selectedConfig.type)">
                      {{ selectedConfig.type }}
                    </div>
                  </div>
                  <div class="detail-description">
                    {{ selectedConfig.description }}
                  </div>
                </div>
              </el-dialog>
            </div>
          </el-tab-pane>
        </el-tabs>
      </MysticalCard>



    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { useMobile } from '@/composables/useMobile'
import { useAuthGuard } from '@/composables/useAuthGuard'
import { ElMessage } from 'element-plus'
import { MysticalCard } from '@/components/common'
import MysticalButton from '@/components/common/MysticalButton.vue'
import { API_CONFIG } from '@/config'
import { NUMBERS_FORTUNE as numbers, getNumberFortune } from '@/data/naming/numbers'
import { getThreeTalentsByElement, getElementStatistics, getConfigsByTianGe, calculateThreeTalents } from '@/data/naming/threeTalents'
import NamingAIReport from './NamingAIReport.vue'
import { lookupChar } from '@/utils/kangxiDict'

// 移动端检测
const { isMobile } = useMobile()
const { requireLogin } = useAuthGuard()

// 输入模式：'char' 汉字输入 | 'manual' 手动输入
const inputMode = ref('char')

// 汉字输入数据
const charInput = reactive({
  surname: '',
  firstName: '',
  secondName: ''
})

// 汉字对应的康熙笔画数
const charStrokes = reactive({
  surname: null,
  firstName: null,
  secondName: null
})

// 是否有未收录的汉字
const hasCharWarning = computed(() => {
  return (charInput.surname && !charStrokes.surname) ||
         (charInput.firstName && !charStrokes.firstName) ||
         (charInput.secondName && !charStrokes.secondName)
})

// 获取汉字的康熙字典笔画（姓名学口径：氵按水4画、艹按艸6画、辶按辵7画等）
// 数据源 utils/kangxiDict（shunshi 康熙字典常用字集），未收录返回 null 走手动输入兜底
const getKangxiStroke = (char) => {
  if (!char || char.trim() === '') return null
  const detail = lookupChar(char.trim())
  return detail ? detail.strokes : null
}

// 汉字输入处理
const onCharInput = (field) => {
  const char = charInput[field]
  if (char && char.trim()) {
    const strokes = getKangxiStroke(char.trim())
    charStrokes[field] = strokes
    // 自动同步笔画到 strokeData
    if (strokes) {
      switch (field) {
        case 'surname':
          strokeData.surnameStrokes = strokes
          break
        case 'firstName':
          strokeData.firstName = strokes
          break
        case 'secondName':
          strokeData.secondName = strokes
          break
      }
    }
  } else {
    charStrokes[field] = null
  }
}


// 笔画数据
const strokeData = reactive({
  surnameStrokes: 1,
  nameType: 'triple', // 'double' | 'triple'
  firstName: 1,
  secondName: 1
})

// 计算结果
const results = ref(null)
const hasResults = computed(() => !!results.value)

// 五格计算结果
const fiveGridsResult = ref(null)
const threeTalentsResult = ref(null)

// 综合评分 + AI 取名报告组件入参（全部由既有数据派生，逻辑在子组件内）
const aiReportData = computed(() => {
  if (!fiveGridsResult.value || !charInput.surname || !charInput.firstName) return null
  const givenStrokes = strokeData.nameType === 'triple'
    ? [strokeData.firstName, strokeData.secondName].filter((n) => Number.isFinite(n) && n > 0)
    : [strokeData.firstName].filter((n) => Number.isFinite(n) && n > 0)
  return {
    surname: charInput.surname,
    givenName: charInput.firstName + (strokeData.nameType === 'triple' ? (charInput.secondName || '') : ''),
    surnameStrokes: strokeData.surnameStrokes,
    givenStrokes,
    grids: fiveGridsResult.value,
    sancaiCombo: threeTalentsResult.value?.combination || ''
  }
})

// 名字横向展示字符
const displayChars = computed(() => {
  const chars = []
  if (charInput.surname) chars.push(charInput.surname)
  if (charInput.firstName) chars.push(charInput.firstName)
  if (charInput.secondName) chars.push(charInput.secondName)
  return chars
})


// 查询工具相关数据
const activeQueryTab = ref('threeTalents')
const numberQuery = ref(null)
const numberQueryResult = ref(null)

// 五行分类查询相关数据
const selectedElement = ref('木')
const elementClassification = ref(null)
const elementStatistics = ref(null)
const configDetailVisible = ref(false)
const selectedConfig = ref(null)

// 天格配置建议相关数据
const tianGeConfigs = ref(null)
const showTianGeConfigs = ref(false)

// 表单验证

// 五格计算验证
const canCalculateGrids = computed(() => {
  if (strokeData.nameType === 'double') {
    return strokeData.surnameStrokes && strokeData.firstName
  } else {
    return strokeData.surnameStrokes && strokeData.firstName && strokeData.secondName
  }
})



// 五格计算函数
const calculateFiveGrids = async () => {
  if (!canCalculateGrids.value) {
    return
  }

  try {
    const { surnameStrokes, firstName, secondName, nameType } = strokeData
    
    let tianGe, renGe, diGe, zongGe, waiGe
    
    if (nameType === 'triple') {
      // 三字名计算公式
      tianGe = surnameStrokes + 1
      renGe = surnameStrokes + firstName
      diGe = firstName + secondName
      zongGe = surnameStrokes + firstName + secondName
      waiGe = secondName + 1
    } else {
      // 双字名计算公式
      tianGe = surnameStrokes + 1
      renGe = surnameStrokes + firstName
      diGe = firstName + 1 // 双字名地格特殊处理
      zongGe = surnameStrokes + firstName
      waiGe = 2 // 双字名外格固定为2
    }

    // 确保数值在1-81范围内
    const normalizeGrid = (value) => {
      while (value > 81) value -= 80
      return value
    }

    fiveGridsResult.value = {
      tianGe: normalizeGrid(tianGe),
      renGe: normalizeGrid(renGe),
      diGe: normalizeGrid(diGe),
      zongGe: normalizeGrid(zongGe),
      waiGe: normalizeGrid(waiGe)
    }

    // 计算三才配置
    calculateThreeTalentsResult()
  } catch (error) {
    console.error('五格计算失败:', error)
    fiveGridsResult.value = null
    threeTalentsResult.value = null
  }
}

// 三才配置计算
const calculateThreeTalentsResult = async () => {
  if (!fiveGridsResult.value) return

  try {
    const { tianGe, renGe, diGe } = fiveGridsResult.value
    
    // 使用threeTalents.js中的计算函数
    const result = calculateThreeTalents(tianGe, renGe, diGe)
    
    if (result) {
      threeTalentsResult.value = {
        combination: result.config,
        type: result.fortune.type,
        description: result.fortune.description
      }
      
      // 自动设置五行分类查询到对应的天格五行
      const tianElement = getElement(tianGe)
      if (tianElement !== selectedElement.value) {
        selectedElement.value = tianElement
      }
      
      // 自动获取天格配置建议
      getTianGeConfigs(tianGe)
    } else {
      threeTalentsResult.value = {
        combination: `${getElement(tianGe)}${getElement(renGe)}${getElement(diGe)}`,
        type: '未知',
        description: '未找到对应的三才配置'
      }
    }
  } catch (error) {
    console.error('三才配置计算失败:', error)
    threeTalentsResult.value = null
  }
}


// 获取姓名输入卡片的格数样式类

// 重置名字笔画
const resetNameStrokes = () => {
  if (strokeData.nameType === 'double') {
    strokeData.secondName = null
  } else {
    strokeData.secondName = 1
  }
  // 自动重新计算
  calculateFiveGrids()
}

// 调整第一字笔画数
const adjustFirstName = (amount) => {
  const newValue = strokeData.firstName + amount
  if (newValue >= 1 && newValue <= 30) {
    strokeData.firstName = newValue
  }
}

// 调整第二字笔画数
const adjustSecondName = (amount) => {
  const newValue = strokeData.secondName + amount
  if (newValue >= 1 && newValue <= 30) {
    strokeData.secondName = newValue
  }
}


// 数理吉凶查询
const queryNumber = async () => {
  if (!numberQuery.value) {
    ElMessage.warning('请输入数理')
    return
  }

  try {
    const result = getNumberFortune(numberQuery.value)
    if (result) {
      numberQueryResult.value = result
      ElMessage.success('数理查询完成')
    } else {
      ElMessage.error('未找到该数理的信息')
    }
  } catch (error) {
    console.error('数理查询失败:', error)
    ElMessage.error('查询失败，请稍后重试')
  }
}

// 选择数理（从速查表）
const selectNumber = (num) => {
  numberQuery.value = num
  queryNumber()
}

// 五行分类查询
const queryElementClassification = async () => {
  if (!selectedElement.value) {
    ElMessage.warning('请选择五行属性')
    return
  }

  try {
    // 获取分类数据
    elementClassification.value = getThreeTalentsByElement(selectedElement.value)
    
    // 获取统计数据
    elementStatistics.value = getElementStatistics(selectedElement.value)
    
    ElMessage.success(`${selectedElement.value}行天格配置查询完成`)
  } catch (error) {
    console.error('五行分类查询失败:', error)
    ElMessage.error('查询失败，请稍后重试')
  }
}

// 查看配置详情
const viewConfigDetail = (config) => {
  selectedConfig.value = config
  configDetailVisible.value = true
}

// 获取百分比样式类
const getPercentageClass = (percentage) => {
  const num = parseInt(percentage)
  if (num >= 60) return 'excellent'
  if (num >= 40) return 'good'
  if (num >= 20) return 'average'
  return 'poor'
}

// 根据天格获取配置建议
const getTianGeConfigs = async (tianGe) => {
  if (!tianGe) {
    tianGeConfigs.value = null
    showTianGeConfigs.value = false
    return
  }

  try {
    // 获取配置建议
    tianGeConfigs.value = getConfigsByTianGe(tianGe)
    showTianGeConfigs.value = true
    
    ElMessage.success(`天格${tianGe}(${tianGeConfigs.value.tianElement}行)的配置建议已生成`)
  } catch (error) {
    console.error('天格配置建议获取失败:', error)
    ElMessage.error('获取配置建议失败，请稍后重试')
  }
}

// 获取分类列样式类
const getClassificationColumnClass = (level) => {
  switch (level) {
    case '大吉': return 'level-excellent'
    case '中吉': return 'level-very-good'
    case '吉': return 'level-good'
    case '吉多于凶': return 'level-mostly-good'
    case '吉凶参半': return 'level-neutral'
    case '凶多于吉': return 'level-mostly-bad'
    case '大凶': return 'level-bad'
    default: return ''
  }
}

// 获取建议等级样式类
const getSuggestionLevelClass = (level) => {
  return getClassificationColumnClass(level)
}

// 获取数理单元格样式类  
const getNumberCellClass = (num) => {
  try {
    // 使用numbers.js中的权威分类数据
    const fortuneType = getGridFortune(num)
    
    switch (fortuneType) {
      case '大吉': return 'fortune-excellent'
      case '吉': return 'fortune-good'
      case '半吉': return 'fortune-average'
      case '凶': return 'fortune-bad'
      default: return 'fortune-bad'
    }
  } catch (error) {
    return 'fortune-bad'
  }
}

// 获取五行属性
const getElement = (number) => {
  const lastDigit = number % 10
  const elementMap = {
    1: '木', 2: '木',
    3: '火', 4: '火',
    5: '土', 6: '土',
    7: '金', 8: '金',
    9: '水', 0: '水'
  }
  return elementMap[lastDigit] || '未知'
}

// 获取数理吉凶
const getGridFortune = (gridValue) => {
  try {
    const number = gridValue > 81 ? gridValue - 80 : gridValue
    const numberData = numbers[number]
    return numberData?.type || '凶'
  } catch (error) {
    return '凶'
  }
}

// 获取数理含义
const getGridMeaning = (gridValue) => {
  try {
    const number = gridValue > 81 ? gridValue - 80 : gridValue
    const numberData = numbers[number]
    return numberData?.meaning || ''
  } catch (error) {
    return ''
  }
}

// 获取格数样式类
const getGridClass = (gridType) => {
  if (!fiveGridsResult.value) return ''
  const fortune = getGridFortune(fiveGridsResult.value[gridType])
  return getFortuneClass(fortune)
}

// 获取三才样式类
const getTalentsClass = (type) => {
  if (type.includes('大吉')) return 'fortune-excellent'
  if (type.includes('吉')) return 'fortune-good'
  if (type.includes('半吉')) return 'fortune-average'
  return 'fortune-bad'
}

// 辅助方法

const getFortuneClass = (type) => {
  switch (type) {
    case '吉':
    case '大吉': return 'fortune-good'
    case '半吉': return 'fortune-average'
    case '凶': return 'fortune-bad'
    default: return 'fortune-neutral'
  }
}

const getGridName = (key) => {
  const names = {
    tianGe: '天格',
    renGe: '人格',
    diGe: '地格',
    waiGe: '外格',
    zongGe: '总格'
  }
  return names[key] || key
}

// 监听笔画数变化，自动计算五格
watch(
  () => [strokeData.surnameStrokes, strokeData.firstName, strokeData.secondName, strokeData.nameType],
  () => {
    if (canCalculateGrids.value) {
      calculateFiveGrids()
    }
  },
  { immediate: true }
)

// 监听选中的五行变化，自动查询
watch(
  selectedElement,
  () => {
    queryElementClassification()
  },
  { immediate: true }
)

// ========== 用户姓名自动加载 ==========
const isOwnName = ref(false)

const loadUserName = async () => {
  let loaded = false
  try {
    const token = localStorage.getItem('token')
    if (token) {
      const res = await fetch(`${API_CONFIG.baseUrl}${API_CONFIG.endpoints.persona.profile}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      const json = await res.json()
      const data = json.data || json
      if (json.success && data && (data.surname || data.real_name)) {
        // 优先使用拆分字段，向后兼容从 real_name 拆分
        const surname = data.surname || (data.real_name ? data.real_name[0] : '')
        const givenName = data.given_name || (data.real_name && data.real_name.length > 1 ? data.real_name.slice(1) : '')
        const chars = [surname, ...Array.from(givenName)].filter(Boolean)
        if (chars.length >= 2) {
          inputMode.value = 'char'
          charInput.surname = chars[0]
          charInput.firstName = chars[1] || ''
          charInput.secondName = chars[2] || ''
          if (chars[2]) {
            strokeData.nameType = 'triple'
          } else {
            strokeData.nameType = 'double'
          }
          onCharInput('surname')
          onCharInput('firstName')
          if (chars[2]) onCharInput('secondName')
          isOwnName.value = true
          loaded = true
        }
      }
    }
  } catch (e) {
    console.log('加载用户姓名失败:', e)
  }

  // 未登录或未填真实姓名，默认显示"张三"作为示例
  if (!loaded) {
    inputMode.value = 'char'
    strokeData.nameType = 'double'
    charInput.surname = '张'
    charInput.firstName = '三'
    onCharInput('surname')
    onCharInput('firstName')
    isOwnName.value = false
  }
}

const clearNameInput = () => {
  charInput.surname = ''
  charInput.firstName = ''
  charInput.secondName = ''
  charStrokes.surname = null
  charStrokes.firstName = null
  charStrokes.secondName = null
  fiveGridsResult.value = null
  threeTalentsResult.value = null
  isOwnName.value = false
}

onMounted(() => {
  loadUserName()
})

defineExpose({
  namingResult: fiveGridsResult,
  talentsResult: threeTalentsResult,
  hasResults: computed(() => !!fiveGridsResult.value),
  fullName: computed(() => {
    if (charInput.surname && charInput.firstName) {
      return charInput.surname + charInput.firstName + (charInput.secondName || '')
    }
    return results.value?.fullName || ''
  }),
  strokeData
})
</script>

<style lang="scss" scoped>
.naming-view {
  padding: 0 16px;
  max-width: 100%;
  position: relative;
}

.own-name-banner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  margin-bottom: 12px;
  border-radius: 8px;
  background: var(--fh-glass-bg);
  backdrop-filter: blur(var(--fh-glass-blur));
  border: 1px solid var(--fh-glass-border);
  font-size: 14px;
  color: var(--fh-text-secondary);
}

.page-motto {
  text-align: center;
  font-size: 0.95rem;
  color: var(--fh-text-secondary);
  font-style: italic;
  margin: 0 0 16px;
}



.results-section {
  position: relative;
  z-index: 2;

  .results-card {
    background: var(--fh-glass-bg);
    backdrop-filter: blur(var(--fh-glass-blur, 10px)) saturate(var(--fh-glass-saturate, 180%));
    border: 1px solid var(--fh-glass-border);

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      color: var(--fh-accent);
      font-size: 1.2rem;
    }

    .score-badge {
      padding: 4px 12px;
      border-radius: 12px;
      font-weight: bold;
      font-size: 1rem;
      
      &.excellent {
        background: linear-gradient(45deg, #4CAF50, #8BC34A);
        color: white;
      }
      
      &.good {
        background: linear-gradient(45deg, #2196F3, #03DAC6);
        color: white;
      }
      
      &.average {
        background: linear-gradient(45deg, #FF9800, #FFC107);
        color: white;
      }
      
      &.poor {
        background: linear-gradient(45deg, #F44336, #E91E63);
        color: white;
      }
    }
  }
}

.section {
  margin-bottom: 24px;
  
  .section-title {
    color: var(--fh-accent);
    font-size: 1.1rem;
    margin-bottom: 12px;
    border-bottom: 1px solid var(--fh-glass-border);
    padding-bottom: 6px;
  }
}

.five-grids {
  .grid-item {
    background: var(--fh-glass-bg);
    border-radius: 8px;
    padding: 16px;
    text-align: center;
    margin-bottom: 16px;
    border: 2px solid transparent;
    transition: all 0.3s ease;
    
    &.fortune-good {
      border-color: #4CAF50;
      background: rgba(76, 175, 80, 0.1);
    }
    
    &.fortune-average {
      border-color: #FF9800;
      background: rgba(255, 152, 0, 0.1);
    }
    
    &.fortune-bad {
      border-color: #F44336;
      background: rgba(244, 67, 54, 0.1);
    }
    
    .grid-name {
      font-size: 0.9rem;
      color: var(--fh-text-secondary);
      margin-bottom: 4px;
    }
    
    .grid-value {
      font-size: 1.8rem;
      font-weight: bold;
      color: var(--fh-accent);
      margin-bottom: 4px;
    }
    
    .grid-type {
      font-size: 0.85rem;
      font-weight: bold;
      margin-bottom: 4px;
      
      &.fortune-good { color: #4CAF50; }
      &.fortune-average { color: #FF9800; }
      &.fortune-bad { color: #F44336; }
    }
    
    .grid-meaning {
      font-size: 0.8rem;
      color: var(--fh-text-secondary);
      opacity: 0.8;
    }
  }
}

.three-talents {
  .talents-display {
    display: flex;
    justify-content: center;
    gap: 24px;
    margin-bottom: 16px;
    
    .talent-item {
      text-align: center;
      
      .talent-name {
        display: block;
        font-size: 0.9rem;
        color: var(--fh-text-secondary);
        margin-bottom: 4px;
      }
      
      .talent-element {
        display: inline-block;
        padding: 8px 16px;
        background: var(--fh-accent);
        color: var(--fh-bg);
        border-radius: 20px;
        font-weight: bold;
        font-size: 1rem;
      }
    }
  }
  
  .talents-result {
    background: var(--fh-glass-bg);
    border-radius: 8px;
    padding: 16px;
    border: 2px solid transparent;
    
    &.fortune-good {
      border-color: #4CAF50;
      background: rgba(76, 175, 80, 0.1);
    }
    
    &.fortune-average {
      border-color: #FF9800;
      background: rgba(255, 152, 0, 0.1);
    }
    
    &.fortune-bad {
      border-color: #F44336;
      background: rgba(244, 67, 54, 0.1);
    }
    
    .result-type {
      font-size: 1.1rem;
      font-weight: bold;
      text-align: center;
      margin-bottom: 8px;
      
      &.fortune-good { color: #4CAF50; }
      &.fortune-average { color: #FF9800; }
      &.fortune-bad { color: #F44336; }
    }
    
    .result-desc {
      color: var(--fh-text);
      line-height: 1.6;
      text-align: center;
    }
  }
}

.suggestions {
  .suggestion-list {
    list-style: none;
    padding: 0;
    
    li {
      background: var(--fh-glass-bg);
      padding: 16px;
      margin-bottom: 8px;
      border-radius: 6px;
      border-left: 3px solid var(--fh-accent);
      color: var(--fh-text);
      line-height: 1.5;
      
      &:before {
        content: "💡 ";
        margin-right: 8px;
      }
    }
  }
}

.calculate-button {
  min-width: 120px;
  padding: 16px 32px;
  
  .icon {
    font-size: 1.2em;
    margin-right: 8px;
  }
}


// 计算器区域样式
.calculator-section {
  background: var(--fh-glass-bg);
  backdrop-filter: blur(var(--fh-glass-blur, 10px)) saturate(var(--fh-glass-saturate, 180%));
  border: 1px solid var(--fh-glass-border);
  margin-bottom: 16px;
  position: relative;
  z-index: 2;

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: var(--fh-accent);
    font-size: 1.2rem;

    .header-tips {
      display: flex;
      align-items: center;
      gap: 12px;
      font-size: 0.85rem;
      color: var(--fh-text-secondary);
      opacity: 0.8;

      .name-type-badge {
        padding: 3px 10px;
        background: var(--fh-accent);
        color: var(--fh-bg);
        border-radius: 12px;
        font-size: 0.85rem;
        opacity: 1;
      }
    }
  }

  .section-divider {
    height: 1px;
    background: linear-gradient(90deg, transparent, var(--fh-accent), transparent);
    margin: 20px 0;
  }

  .section-title {
    color: var(--fh-accent);
    margin-bottom: 12px;
    text-align: center;
    font-size: 1.1rem;
  }
}

// 康熙字典提示
.kangxi-tip {
  font-size: 0.8rem;
  color: var(--fh-text-secondary);
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 12px;
  line-height: 1.4;
}

// 输入模式切换
.input-mode-switch {
  margin-bottom: 10px;

  :deep(.el-radio-button__inner) {
    background: var(--fh-glass-bg);
    border-color: var(--fh-accent);
    color: var(--fh-text);
    padding: 6px 14px;
    font-size: 0.85rem;
  }
  
  :deep(.el-radio-button__original-radio:checked + .el-radio-button__inner) {
    background: var(--fh-accent);
    color: var(--fh-bg);
  }
}

// 姓名类型行
.name-type-row {
  margin-bottom: 12px;

  :deep(.el-radio-button__inner) {
    background: var(--fh-glass-bg);
    border-color: var(--fh-accent);
    color: var(--fh-text);
    padding: 6px 14px;
    font-size: 0.85rem;
  }
  
  :deep(.el-radio-button__original-radio:checked + .el-radio-button__inner) {
    background: var(--fh-accent);
    color: var(--fh-bg);
  }
}

// 汉字输入模式
.char-input-mode {
  .char-input-row {
    display: flex;
    gap: 12px;
    justify-content: center;
    flex-wrap: wrap;
  }

  .char-input-item {
    flex: 1;
    min-width: 120px;
    max-width: 180px;

    .char-label {
      display: block;
      font-size: 0.8rem;
      color: var(--fh-text-secondary);
      margin-bottom: 4px;
      text-align: center;
    }

    .char-input-wrapper {
      display: flex;
      flex-direction: column;
      gap: 4px;
      align-items: center;

      .char-input {
        :deep(.el-input__wrapper) {
          background: var(--fh-glass-bg);
          border: 1px solid var(--fh-accent);
          border-radius: 6px;
          box-shadow: none !important;
        }

        :deep(.el-input__inner) {
          background: transparent;
          color: var(--fh-text);
          text-align: center;
          font-size: 1.1rem;
          height: 36px;
        }
      }

      .stroke-tag {
        font-size: 0.75rem;
        color: #4CAF50;
        background: rgba(76, 175, 80, 0.1);
        padding: 2px 8px;
        border-radius: 10px;
        white-space: nowrap;

        &.tag-warning {
          color: #FF9800;
          background: rgba(255, 152, 0, 0.1);
        }
      }
    }
  }

  .char-input-hint {
    font-size: 0.8rem;
    color: #FF9800;
    text-align: center;
    margin-top: 8px;
  }
}

// 手动输入提示
.manual-tip {
  font-size: 0.8rem;
  color: var(--fh-text-secondary);
  text-align: center;
  margin-bottom: 12px;
  opacity: 0.8;
}

.stroke-calculator {
  .calculator-row {
    display: flex;
    gap: 24px;
    align-items: flex-start;
    justify-content: center;
  }

  .calculator-column {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    min-width: 120px;
    flex: 0 0 auto;

    .column-header {
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;

      :is(h4) {
        color: var(--fh-accent);
        margin: 0;
        font-size: 0.95rem;
        text-align: center;
      }
    }

    .name-type-tabs {
      :deep(.el-radio-button__inner) {
        background: var(--fh-glass-bg);
        border-color: var(--fh-accent);
        color: var(--fh-text);
        padding: 8px 16px;
        font-size: 0.9rem;
      }
      
      :deep(.el-radio-button__original-radio:checked + .el-radio-button__inner) {
        background: var(--fh-accent);
        color: var(--fh-bg);
      }
    }

    .placeholder-space {
      height: 60px;
    }
  }

  .stroke-input-group {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    min-width: 140px;

    .input-with-label {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      justify-content: center;

      :deep(.el-input-number) {
        width: 100px;
        
        .el-input__wrapper {
          background: var(--fh-glass-bg);
          border-color: var(--fh-accent);
          color: var(--fh-text);
          text-align: center;
        }
        
        .el-input__inner {
          background: transparent;
          border: none;
          color: var(--fh-text);
          text-align: center;
          height: 32px;
        }
        
        .el-input-number__increase,
        .el-input-number__decrease {
          background: var(--fh-glass-bg);
          border-color: var(--fh-accent);
          color: var(--fh-text);
          
          &:hover {
            background: var(--fh-accent);
            color: var(--fh-bg);
          }
        }
      }

      .input-label {
        color: var(--fh-text-secondary);
        font-size: 0.85rem;
        font-weight: 500;
        min-width: 20px;
      }
    }

    .quick-buttons {
      display: flex;
      gap: 4px;
      margin-top: 2px;
      width: 100px;
      justify-content: space-between;

      .adjust-button {
        flex: 1;
        height: 32px;
        padding: 0;
        font-size: 0.8rem;
        border-radius: 4px;
        background: var(--fh-glass-bg);
        border: 1px solid var(--fh-glass-border);
        color: var(--fh-text);
        transition: all 0.3s ease;
        min-width: 0;
        
        &:hover:not(:disabled) {
          background: var(--fh-accent);
          color: var(--fh-bg);
          transform: translateY(-1px);
        }
        
        &:disabled {
          opacity: 0.4;
          cursor: not-allowed;
          background: var(--fh-glass-bg);;
          border-color: rgba(255, 255, 255, 0.2);
        }
        
        &:active:not(:disabled) {
          transform: translateY(0);
        }
      }
    }
  }

}



.grids-display {
  .grid-row {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 12px;
    margin-bottom: 12px;

    .grid-item {
      background: var(--fh-glass-bg);
      border-radius: 8px;
      padding: 12px 8px;
      text-align: center;
      border: 2px solid transparent;
      transition: all 0.3s ease;

      &.fortune-good {
        border-color: #4CAF50;
        background: rgba(76, 175, 80, 0.1);
      }

      &.fortune-average {
        border-color: #FF9800;
        background: rgba(255, 152, 0, 0.1);
      }

      &.fortune-bad {
        border-color: #F44336;
        background: rgba(244, 67, 54, 0.1);
      }

      &.fortune-excellent {
        border-color: #9C27B0;
        background: rgba(156, 39, 176, 0.1);
      }

      .grid-name {
        font-size: 0.8rem;
        color: var(--fh-text-secondary);
        margin-bottom: 2px;
      }

      .grid-value {
        font-size: 1.5rem;
        font-weight: bold;
        color: var(--fh-accent);
        margin-bottom: 2px;
      }

      .grid-element {
        font-size: 0.9rem;
        font-weight: bold;
        margin-bottom: 2px;
        color: var(--fh-card);
      }

      .grid-fortune {
        font-size: 0.75rem;
        font-weight: bold;

        &:contains('吉') { color: #4CAF50; }
        &:contains('半吉') { color: #FF9800; }
        &:contains('凶') { color: #F44336; }
      }
    }
  }
}

// 新的三才配置布局样式
.talents-layout-section {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid var(--fh-glass-border);

  .section-title {
    color: var(--fh-accent);
    margin-bottom: 16px;
    text-align: center;
    font-size: 1.1rem;
  }

  .talents-grid-layout {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    max-width: 100%;
    margin: 0 auto;

    .grid-card {
      background: var(--fh-glass-bg);
      border-radius: 8px;
      padding: 12px;
      border: 1px solid rgba(255, 255, 255, 0.1);
      transition: all 0.3s ease;

      &:hover {
        background: var(--fh-glass-bg);;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      }
    }

    // 右侧附加格数卡片样式
    .additional-grids {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
      align-self: stretch;

      .number-query-result {
        background: var(--fh-glass-bg);
        border-radius: 8px;
        padding: 12px;
        display: flex;
        flex-direction: column;

        .result-header {
          display: flex;
          gap: 8px;
          align-items: center;
          margin-bottom: 8px;

          .number-value {
            font-size: 1.5rem;
            font-weight: bold;
            color: var(--fh-accent);
            padding: 8px 16px;
            background: rgba(255, 255, 255, 0.1);
            border-radius: 8px;
          }

          .number-name {
            font-size: 1.1rem;
            font-weight: bold;
            color: var(--fh-card);
          }

          .number-type {
            padding: 4px 12px;
            border-radius: 12px;
            font-weight: bold;
            font-size: 0.9rem;

            &.fortune-good { 
              background: linear-gradient(45deg, #4CAF50, #A5D6A7);
              color: white;
            }
            &.fortune-average { 
              background: linear-gradient(45deg, #FF9800, #FFCC02);
              color: white;
            }
            &.fortune-bad { 
              background: linear-gradient(45deg, #F44336, #EF5350);
              color: white;
            }
            &.fortune-neutral {
              background: linear-gradient(45deg, #9E9E9E, #BDBDBD);
              color: white;
            }
          }
        }

        .number-meaning,
        .number-description {
          margin-bottom: 16px;
          color: var(--fh-text);
          line-height: 1.6;

          strong {
            color: var(--fh-accent);
          }
        }

        .number-description {
          margin-bottom: 0;
          flex: 1;
          color: var(--fh-text-secondary);
          font-size: 0.85rem;
          line-height: 1.5;

          .grid-role {
            display: block;
            color: var(--fh-text);
            font-weight: 600;
            font-size: 0.8rem;
            margin-bottom: 4px;
          }
        }
      }
    }

    // 装饰卡片样式
    .decorative-card {
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--fh-glass-bg);;
      border: 1px dashed rgba(255, 255, 255, 0.1);

      .decorative-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 4px;

        .decorative-icon {
          font-size: 1.5rem;
          opacity: 0.6;
        }

        .decorative-text {
          font-size: 0.8rem;
          color: var(--fh-text-secondary);
          opacity: 0.6;
        }
      }
    }

    // 主要三才配置卡片样式
    .main-talents-card {
      background: var(--fh-glass-bg);
      border: 2px solid var(--fh-glass-border);
      
      .talents-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;
        padding-bottom: 8px;
        border-bottom: 1px solid var(--fh-glass-border);

        h5 {
          color: var(--fh-accent);
          margin: 0;
          font-size: 1.1rem;
        }

        .config-type {
          padding: 4px 12px;
          border-radius: 12px;
          font-weight: bold;
          font-size: 0.9rem;

          &.fortune-excellent {
            background: linear-gradient(45deg, #9C27B0, #E1BEE7);
            color: white;
          }

          &.fortune-good {
            background: linear-gradient(45deg, #4CAF50, #A5D6A7);
            color: white;
          }

          &.fortune-average {
            background: linear-gradient(45deg, #FF9800, #FFCC02);
            color: white;
          }

          &.fortune-bad {
            background: linear-gradient(45deg, #F44336, #EF5350);
            color: white;
          }
        }
      }

      .talents-combination {
        display: flex;
        justify-content: space-around;
        margin-bottom: 16px;

        .talent-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;

          .talent-label {
            font-size: 0.8rem;
            color: var(--fh-text-secondary);
          }

          .talent-element {
            background: var(--fh-accent);
            color: var(--fh-bg);
            padding: 6px 12px;
            border-radius: 16px;
            font-weight: bold;
            font-size: 0.9rem;
          }
        }
      }

      .talents-description {
        color: var(--fh-text);
        line-height: 1.5;
        font-size: 0.9rem;
        text-align: center;
        padding: 8px;
        background: var(--fh-glass-bg);;
        border-radius: 6px;
        border-left: 3px solid var(--fh-accent);
      }
    }
  }
}

// 查询工具区域样式
.query-tools-section {
  background: var(--fh-glass-bg);
  backdrop-filter: blur(var(--fh-glass-blur, 10px)) saturate(var(--fh-glass-saturate, 180%));
  border: 1px solid var(--fh-glass-border);
  margin-bottom: 16px;
  position: relative;
  z-index: 2;

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: var(--fh-accent);
    font-size: 1.2rem;
  }
}

.query-tabs {
  :deep(.el-tabs__header) {
    margin-bottom: 16px;
  }

  :deep(.el-tabs__nav-wrap) {
    &::after {
      background-color: var(--fh-accent);
    }
  }

  :deep(.el-tabs__item) {
    color: var(--fh-text-secondary);
    font-size: 1rem;

    &.is-active {
      color: var(--fh-accent);
    }

    &:hover {
      color: var(--fh-accent);
    }
  }

  :deep(.el-tabs__active-bar) {
    background-color: var(--fh-accent);
  }
}

.query-tab-pane {
  padding: 16px 0;
}

// 三才配置查询样式
.talents-query-section {
  .query-form {
    margin-bottom: 20px;

    h4 {
      color: var(--fh-accent);
      margin-bottom: 12px;
    }
  }
  
  // 天格配置建议样式
  .tiange-suggestions-section {
    margin-bottom: 20px;
    
    .section-header {
      text-align: center;
      margin-bottom: 24px;
      
      h4 {
        color: var(--fh-accent);
        font-size: 1.3rem;
        margin-bottom: 8px;
      }
      
      .section-desc {
        color: var(--fh-text-secondary);
        font-size: 0.95rem;
        opacity: 0.9;
        margin: 0;
      }
    }
    
    .suggestions-display {
      display: flex;
      flex-direction: column;
      gap: 16px;
      
      .suggestion-level {
        background: var(--fh-glass-bg);
        border-radius: 8px;
        padding: 16px;
        border: 2px solid transparent;
        
        &.level-excellent {
          border-color: #9C27B0;
          background: rgba(156, 39, 176, 0.1);
        }
        
        &.level-very-good {
          border-color: #4CAF50;
          background: rgba(76, 175, 80, 0.1);
        }
        
        &.level-good {
          border-color: #2196F3;
          background: rgba(33, 150, 243, 0.1);
        }
        
        &.level-mostly-good {
          border-color: #03DAC6;
          background: rgba(3, 218, 198, 0.1);
        }
        
        &.level-neutral {
          border-color: #FF9800;
          background: rgba(255, 152, 0, 0.1);
        }
        
        &.level-mostly-bad {
          border-color: #FF5722;
          background: rgba(255, 87, 34, 0.1);
        }
        
        &.level-bad {
          border-color: #F44336;
          background: rgba(244, 67, 54, 0.1);
        }
        
        .level-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          padding-bottom: 8px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          
          h5 {
            color: var(--fh-accent);
            margin: 0;
            font-size: 1.1rem;
          }
          
          .count-badge {
            background: var(--fh-accent);
            color: var(--fh-bg);
            padding: 3px 10px;
            border-radius: 12px;
            font-size: 0.8rem;
            font-weight: bold;
          }
        }
        
        .configs-list {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;

          .config-suggestion {
            background: var(--fh-glass-bg);
            border-radius: 6px;
            padding: 12px;
            border: 1px solid rgba(255, 255, 255, 0.1);
            display: flex;
            flex-direction: column;
            
            .config-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 8px;
              
              .config-name {
                font-weight: bold;
                color: var(--fh-card);
                font-size: 1.1rem;
              }
              
              .config-elements {
                display: flex;
                gap: 4px;
                
                .element {
                  background: var(--fh-accent);
                  color: var(--fh-bg);
                  padding: 3px 8px;
                  border-radius: 10px;
                  font-size: 0.8rem;
                  font-weight: bold;
                }
              }
            }
            
            .config-examples {
              margin-bottom: 8px;
              
              .example-item {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 8px;
                background: var(--fh-glass-bg);;
                border-radius: 4px;
                margin-bottom: 4px;
                
                &:last-child {
                  margin-bottom: 0;
                }
                
                .combination {
                  font-weight: bold;
                  color: var(--fh-accent);
                  font-size: 1rem;
                  font-family: monospace;
                }
                
                .description {
                  color: var(--fh-text-secondary);
                  font-size: 0.85rem;
                }
              }
            }
            
            .config-description {
              color: var(--fh-text);
              font-size: 0.9rem;
              line-height: 1.5;
              opacity: 0.9;
              padding: 8px;
              background: var(--fh-glass-bg);;
              border-radius: 4px;
              border-left: 3px solid var(--fh-accent);
            }
          }
        }
      }
    }
  }

  .talents-input-group {
    display: flex;
    gap: 16px;
    align-items: end;
    flex-wrap: wrap;

    .talent-input {
      display: flex;
      flex-direction: column;
      gap: 8px;

      label {
        color: var(--fh-text-secondary);
        font-size: 0.9rem;
      }

      :deep(.el-input-number) {
        width: 120px;

        .el-input__inner {
          background: var(--fh-glass-bg);
          border-color: var(--fh-accent);
          color: var(--fh-text);
        }
      }
    }

    .query-button {
      margin-left: 16px;
    }
  }

  .talents-query-result {
    background: var(--fh-glass-bg);
    border-radius: 8px;
    padding: 24px;
    border: 2px solid var(--fh-glass-border);

    .result-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;

      .config-name {
        font-size: 1.2rem;
        font-weight: bold;
        color: var(--fh-card);
      }

      .config-type {
        padding: 4px 12px;
        border-radius: 12px;
        font-weight: bold;
        font-size: 0.9rem;
      }
    }

    .elements-display {
      display: flex;
      gap: 16px;
      margin-bottom: 16px;

      .element-item {
        padding: 6px 12px;
        background: var(--fh-accent);
        color: var(--fh-bg);
        border-radius: 16px;
        font-size: 0.9rem;
      }
    }

    .result-description {
      color: var(--fh-text);
      line-height: 1.6;
      font-size: 0.95rem;
    }
  }
}

// 数理吉凶查询样式
.numbers-query-section {
  .query-form {
    margin-bottom: 20px;

    h4 {
      color: var(--fh-accent);
      margin-bottom: 12px;
    }
  }

  .number-input-group {
    display: flex;
    gap: 16px;
    align-items: center;

    :deep(.el-input-number) {
      .el-input__inner {
        background: var(--fh-glass-bg);
        border-color: var(--fh-accent);
        color: var(--fh-text);
      }
    }
  }

  .number-query-result {
    background: var(--fh-glass-bg);
    border-radius: 8px;
    padding: 16px;
    border: 2px solid var(--fh-glass-border);
    margin-bottom: 20px;

    .result-header {
      display: flex;
      gap: 12px;
      align-items: center;
      margin-bottom: 12px;

      .number-value {
        font-size: 1.5rem;
        font-weight: bold;
        color: var(--fh-accent);
        padding: 6px 14px;
        background: rgba(255, 255, 255, 0.1);
        border-radius: 8px;
      }

      .number-name {
        font-size: 1.1rem;
        font-weight: bold;
        color: var(--fh-card);
      }

      .number-type {
        padding: 3px 10px;
        border-radius: 12px;
        font-weight: bold;
        font-size: 0.85rem;
      }
    }

    .number-meaning,
    .number-description {
      margin-bottom: 12px;
      color: var(--fh-text);
      line-height: 1.6;

      strong {
        color: var(--fh-accent);
      }
    }
  }

  .numbers-quick-reference {
    h4 {
      color: var(--fh-accent);
      margin-bottom: 12px;
    }

    .numbers-grid {
      display: grid;
      grid-template-columns: repeat(9, 1fr);
      gap: 4px;
      margin-bottom: 12px;
      padding: 8px;
      background: var(--fh-glass-bg);
      border-radius: 8px;

      .number-cell {
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 6px;
        cursor: pointer;
        font-size: 0.9rem;
        font-weight: bold;
        transition: all 0.3s ease;
        color: white;

        &:hover {
          transform: scale(1.1);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
        }

        &.fortune-excellent {
          background: linear-gradient(45deg, #9C27B0, #E1BEE7);
        }

        &.fortune-good {
          background: linear-gradient(45deg, #4CAF50, #A5D6A7);
        }

        &.fortune-average {
          background: linear-gradient(45deg, #FF9800, #FFCC02);
        }

        &.fortune-bad {
          background: linear-gradient(45deg, #F44336, #EF5350);
        }
      }
    }

    .legend {
      display: flex;
      gap: 16px;
      justify-content: center;

      .legend-item {
        padding: 4px 12px;
        border-radius: 12px;
        font-size: 0.85rem;
        font-weight: bold;
        color: white;

        &.fortune-excellent {
          background: linear-gradient(45deg, #9C27B0, #E1BEE7);
        }

        &.fortune-good {
          background: linear-gradient(45deg, #4CAF50, #A5D6A7);
        }

        &.fortune-average {
          background: linear-gradient(45deg, #FF9800, #FFCC02);
        }

        &.fortune-bad {
          background: linear-gradient(45deg, #F44336, #EF5350);
        }
      }
    }
  }
}

// 五行分类查询样式
.element-classification-section {
  .query-form {
    margin-bottom: 20px;

    h4 {
      color: var(--fh-accent);
      margin-bottom: 12px;
    }
  }

  .element-selector {
    :deep(.el-radio-button__inner) {
      background: var(--fh-glass-bg);
      border-color: var(--fh-accent);
      color: var(--fh-text);
      padding: 10px 20px;
      font-size: 1rem;
      font-weight: bold;
    }
    
    :deep(.el-radio-button__original-radio:checked + .el-radio-button__inner) {
      background: var(--fh-accent);
      color: var(--fh-bg);
    }
  }

  .element-statistics {
    background: var(--fh-glass-bg);
    border-radius: 8px;
    padding: 16px;
    border: 2px solid var(--fh-glass-border);
    margin-bottom: 20px;

    h4 {
      color: var(--fh-accent);
      margin-bottom: 12px;
      text-align: center;
    }

    .stats-row {
      display: flex;
      justify-content: space-around;
      flex-wrap: wrap;
      gap: 12px;

      .stat-item {
        text-align: center;

        .stat-label {
          display: block;
          color: var(--fh-text-secondary);
          font-size: 0.9rem;
          margin-bottom: 4px;
        }

        .stat-value {
          display: block;
          font-size: 1.2rem;
          font-weight: bold;
          color: var(--fh-card);

          &.good { color: #4CAF50; }
          &.excellent { color: #9C27B0; }
          &.average { color: #FF9800; }
          &.poor { color: #F44336; }
        }
      }
    }
  }

  .element-classification-display {
    .classification-columns {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
      margin-bottom: 20px;

      .classification-column {
        flex: 1;
        min-width: 200px;
        background: var(--fh-glass-bg);
        border-radius: 8px;
        padding: 16px;
        border: 2px solid transparent;

        &.level-excellent {
          border-color: #9C27B0;
          background: rgba(156, 39, 176, 0.1);
        }

        &.level-very-good {
          border-color: #4CAF50;
          background: rgba(76, 175, 80, 0.1);
        }

        &.level-good {
          border-color: #2196F3;
          background: rgba(33, 150, 243, 0.1);
        }

        &.level-mostly-good {
          border-color: #03DAC6;
          background: rgba(3, 218, 198, 0.1);
        }

        &.level-neutral {
          border-color: #FF9800;
          background: rgba(255, 152, 0, 0.1);
        }

        &.level-mostly-bad {
          border-color: #FF5722;
          background: rgba(255, 87, 34, 0.1);
        }

        &.level-bad {
          border-color: #F44336;
          background: rgba(244, 67, 54, 0.1);
        }

        .column-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          padding-bottom: 8px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);

          h5 {
            color: var(--fh-accent);
            margin: 0;
            font-size: 1rem;
          }

          .count-badge {
            background: var(--fh-accent);
            color: var(--fh-bg);
            padding: 2px 8px;
            border-radius: 12px;
            font-size: 0.8rem;
            font-weight: bold;
          }
        }

        .config-list {
          .config-item {
            background: var(--fh-glass-bg);;
            border-radius: 6px;
            padding: 8px;
            margin-bottom: 8px;
            cursor: pointer;
            transition: all 0.3s ease;

            &:hover {
              background: rgba(255, 255, 255, 0.1);
              transform: translateY(-2px);
            }

            .config-name {
              font-weight: bold;
              color: var(--fh-card);
              margin-bottom: 4px;
              font-size: 1rem;
            }

            .config-elements {
              display: flex;
              gap: 4px;

              .element {
                background: var(--fh-accent);
                color: var(--fh-bg);
                padding: 2px 6px;
                border-radius: 10px;
                font-size: 0.8rem;
                font-weight: bold;
              }
            }
          }
        }
      }
    }
  }
}

// 配置详情弹窗样式
:deep(.config-detail-dialog) {
  .el-dialog__body {
    padding: 32px;
  }

  .config-detail {
    .detail-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
      padding-bottom: 16px;
      border-bottom: 2px solid var(--fh-glass-border);

      .config-elements-large {
        display: flex;
        gap: 16px;

        .element-large {
          background: var(--fh-accent);
          color: var(--fh-bg);
          padding: 12px 20px;
          border-radius: 20px;
          font-size: 1.2rem;
          font-weight: bold;
        }
      }

      .config-type {
        padding: 8px 16px;
        border-radius: 16px;
        font-weight: bold;
        font-size: 1.1rem;

        &.fortune-good { 
          background: linear-gradient(45deg, #4CAF50, #A5D6A7);
          color: white;
        }
        &.fortune-average { 
          background: linear-gradient(45deg, #FF9800, #FFCC02);
          color: white;
        }
        &.fortune-bad { 
          background: linear-gradient(45deg, #F44336, #EF5350);
          color: white;
        }
        &.fortune-neutral {
          background: linear-gradient(45deg, #9E9E9E, #BDBDBD);
          color: white;
        }
      }
    }

    .detail-description {
      color: var(--fh-text);
      line-height: 1.8;
      font-size: 1rem;
      text-align: justify;
    }
  }
}

@media (max-width: 768px) {
  .naming-view {
    padding: 16px;
  }



  .stroke-calculator {
    .calculator-row {
      flex-direction: column;
      gap: 24px;
      align-items: center;
    }

    .calculator-column {
      min-width: 100px;
      
      .column-header {
        h4 {
          font-size: 0.9rem;
        }
      }
      
      .name-type-tabs {
        :deep(.el-radio-button__inner) {
          padding: 6px 12px;
          font-size: 0.85rem;
        }
      }
      
      .stroke-input-group {
        min-width: 120px;
        
        .input-with-label {
          :deep(.el-input-number) {
            width: 90px;
            
            .el-input__wrapper {
              background: var(--fh-glass-bg);
              border-color: var(--fh-accent);
            }
            
            .el-input__inner {
              height: 28px;
              background: transparent;
              border: none;
            }
          }
          
          .input-label {
            font-size: 0.8rem;
            min-width: 18px;
          }
        }
        
        .quick-buttons {
          width: 90px; /* 与移动端 el-input-number 宽度一致 */
          margin-top: 3px;
          
          .adjust-button {
            flex: 1;
            height: 28px; /* 与移动端 el-input__wrapper 高度一致 */
            font-size: 0.75rem;
            padding: 0;
            min-width: 0;
          }
        }
      }
    }
  }

  .grids-display .grid-row {
    grid-template-columns: repeat(3, 1fr);

    .grid-item {
      margin-bottom: 0;
    }
  }

  // 移动端三才配置布局调整
  .talents-layout-section {
    .talents-grid-layout {
      grid-template-columns: 1fr;
      gap: 16px;
      max-width: 100%;

      .grid-card {
        padding: 8px;
      }

      .main-talents-card {
        .talents-header {
          flex-direction: column;
          gap: 4px;
          text-align: center;

          .config-type {
            font-size: 0.8rem;
            padding: 3px 10px;
          }
        }

        .talents-combination {
          flex-direction: column;
          gap: 8px;

          .talent-item {
            flex-direction: row;
            justify-content: space-between;

            .talent-label {
              font-size: 0.75rem;
            }

            .talent-element {
              padding: 4px 10px;
              font-size: 0.8rem;
            }
          }
        }

        .talents-description {
          font-size: 0.85rem;
          padding: 4px;
        }
      }

      .additional-grids {
        grid-template-columns: 1fr;
        gap: 16px;

        .number-query-result {
          padding: 16px;

          .result-header {
            flex-wrap: wrap;
            gap: 8px;

            .number-value {
              font-size: 1.3rem;
              padding: 6px 12px;
            }

            .number-name {
              font-size: 1rem;
            }

            .number-type {
              font-size: 0.8rem;
              padding: 3px 10px;
            }
          }

          .number-meaning,
          .number-description {
            font-size: 0.85rem;
            margin-bottom: 8px;
          }
        }
      }
    }
  }

  .talents-input-group {
    flex-direction: column;
    align-items: stretch;

    .query-button {
      margin-left: 0;
      margin-top: 16px;
    }
  }

  .number-input-group {
    flex-direction: column;
    align-items: stretch;
  }

  .numbers-grid {
    grid-template-columns: repeat(9, 1fr);

    .number-cell {
      width: 100%;
      height: 32px;
      font-size: 0.75rem;
    }
  }

  .legend {
    flex-wrap: wrap;
    gap: 6px;
  }

  // 汉字输入模式移动端
  .char-input-mode {
    .char-input-row {
      flex-direction: column;
      align-items: center;
    }

    .char-input-item {
      max-width: 100%;
      min-width: 0;
      width: 100%;
    }
  }

  // 五行分类移动端适配
  .element-classification-section {
    .classification-columns {
      flex-direction: column;
      gap: 8px;

      .classification-column {
        min-width: 100%;
      }
    }

    .element-statistics .stats-row {
      flex-direction: column;
      gap: 8px;
    }

    .element-selector {
      :deep(.el-radio-button__inner) {
        padding: 8px 12px;
        font-size: 0.9rem;
      }
    }
  }
  
  // 天格配置建议移动端适配
  .talents-query-section {
    .tiange-suggestions-section {
      .suggestions-display {
        gap: 12px;
        
        .suggestion-level {
          .configs-list {
            grid-template-columns: 1fr;
          }
          .config-suggestion {
            .config-header {
              flex-direction: column;
              align-items: flex-start;
              gap: 8px;
            }
            
            .config-examples {
              .example-item {
                flex-direction: column;
                align-items: flex-start;
                gap: 4px;
                
                .combination {
                  font-size: 1.1rem;
                }
                
                .description {
                  font-size: 0.8rem;
                }
              }
            }
          }
        }
      }
    }
  }

  :deep(.config-detail-dialog) {
    .config-detail .detail-header {
      flex-direction: column;
      gap: 16px;
      text-align: center;

      .config-elements-large {
        justify-content: center;
      }
    }
  }
}

/* ============================================
   移动端独立模板样式 (.mobile- 前缀)
   白底 + 红色强调 #e60023 + 黑色文字
   ============================================ */

.mobile-naming-view {
  padding: var(--mobile-md);
  padding-bottom: var(--mobile-bottom-padding);
  font-size: var(--mobile-font-base);
  color: var(--mobile-text-primary);
  background: #fff;
  min-height: 100vh;
}

.mobile-motto {
  text-align: center;
  font-size: var(--mobile-font-sm);
  color: var(--mobile-text-hint);
  margin: 0 0 var(--mobile-md);
}

.mobile-banner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--mobile-sm) var(--mobile-md);
  margin-bottom: var(--mobile-md);
  background: var(--mobile-accent-light);
  border: 1px solid var(--mobile-accent);
  border-radius: var(--mobile-radius-sm);
  font-size: var(--mobile-font-sm);
  color: var(--mobile-text-primary);
}

/* ---------- 紧凑输入卡片 ---------- */
.mobile-input-card {
  background: var(--mobile-bg-card);
  border: 1px solid var(--mobile-border-color);
  border-radius: var(--mobile-radius-md);
  padding: var(--mobile-md);
  margin-bottom: var(--mobile-lg);
  box-shadow: var(--mobile-shadow-card);
}

.mobile-input-top-row {
  display: flex;
  gap: var(--mobile-sm);
  align-items: flex-end;
  margin-bottom: var(--mobile-md);
}

.mobile-field {
  flex: 1;

  .mobile-label {
    display: block;
    font-size: var(--mobile-font-sm);
    color: var(--mobile-text-secondary);
    margin-bottom: 4px;
  }
}

.mobile-field--type {
  flex: 0 0 auto;

  :deep(.el-radio-button__inner) {
    padding: 6px 12px;
    font-size: var(--mobile-font-sm);
    border-color: var(--mobile-border-color);
    color: var(--mobile-text-primary);
  }

  :deep(.el-radio-button__original-radio:checked + .el-radio-button__inner) {
    background: var(--mobile-accent);
    border-color: var(--mobile-accent);
    color: #fff;
  }
}

.mobile-input {
  :deep(.el-input__wrapper) {
    border-radius: var(--mobile-radius-sm);
    border: 1px solid var(--mobile-border-color);
    box-shadow: none !important;
  }

  :deep(.el-input__inner) {
    font-size: var(--mobile-font-lg);
    text-align: center;
    height: 40px;
  }
}

/* ---------- 分步笔画输入 ---------- */
.mobile-stroke-step {
  padding: var(--mobile-sm) 0;
  border-top: 1px solid var(--mobile-border-color);

  &:first-of-type {
    border-top: none;
  }
}

.mobile-step-header {
  display: flex;
  align-items: center;
  gap: var(--mobile-sm);
  margin-bottom: var(--mobile-sm);
}

.mobile-step-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: var(--mobile-accent);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
}

.mobile-step-label {
  font-size: var(--mobile-font-sm);
  color: var(--mobile-text-secondary);
}

.mobile-stroke-info {
  margin-left: auto;
  font-size: var(--mobile-font-sm);
  color: #4CAF50;
  font-weight: 500;
}

.mobile-stroke-warn {
  margin-left: auto;
  font-size: var(--mobile-font-sm);
  color: #FF9800;
  font-weight: 500;
}

.mobile-step-row {
  display: flex;
  align-items: center;
  gap: var(--mobile-sm);
}

.mobile-num-input {
  flex: 0 0 100px;

  :deep(.el-input__wrapper) {
    border-radius: var(--mobile-radius-sm);
    border: 1px solid var(--mobile-border-color);
    box-shadow: none !important;
  }
}

.mobile-input-unit {
  font-size: var(--mobile-font-sm);
  color: var(--mobile-text-hint);
}

.mobile-char-hint {
  margin-top: var(--mobile-sm);
  font-size: var(--mobile-font-sm);
  color: #FF9800;
}

/* ---------- 移动端：名字横向展示 ---------- */
.mobile-name-display {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin: var(--mobile-md) 0;
  padding: var(--mobile-md);
  background: var(--mobile-bg-card);
  border: 1px solid var(--mobile-border-color);
  border-radius: var(--mobile-radius-md);
}

.mobile-name-char {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  font-size: 28px;
  font-weight: 700;
  color: var(--mobile-accent);
  border: 2px solid var(--mobile-border-color);
  border-radius: var(--mobile-radius-sm);
  background: var(--mobile-bg-card);

  &.char-surname {
    color: var(--mobile-text-primary);
    border-color: var(--mobile-accent);
    background: rgba(var(--mobile-accent-rgb, 123,44,191), 0.08);
  }
}

/* ---------- 五格纵向卡片堆叠 ---------- */
.mobile-grids-section {
  margin-bottom: var(--mobile-lg);
}

.mobile-section-title {
  font-size: var(--mobile-font-lg);
  color: var(--mobile-text-primary);
  margin: 0 0 var(--mobile-md);
}

.mobile-grids-list {
  display: flex;
  flex-direction: row;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  gap: 10px;
  padding-bottom: 8px;
}

.mobile-grid-card {
  flex-shrink: 0;
  width: 140px;
  scroll-snap-align: start;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 12px 8px;
  background: var(--mobile-bg-card);
  border: 1px solid var(--mobile-border-color);
  border-radius: var(--mobile-radius-sm);
  border-top: 3px solid var(--mobile-border-color);
  transition: border-color 0.2s;

  &.fortune-good {
    border-top-color: #4CAF50;
  }

  &.fortune-average {
    border-top-color: #FF9800;
  }

  &.fortune-bad {
    border-top-color: #F44336;
  }

  &.fortune-excellent {
    border-top-color: #9C27B0;
  }
}

.mobile-grid-label {
  font-size: var(--mobile-font-sm);
  color: var(--mobile-text-secondary);
}

.mobile-grid-value {
  font-size: var(--mobile-font-xl);
  font-weight: 700;
  color: var(--mobile-accent);
}

.mobile-grid-element {
  font-size: var(--mobile-font-sm);
  color: var(--mobile-text-secondary);
}

.mobile-grid-fortune {
  font-size: 11px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 8px;

  &.fortune-good,
  &.fortune-excellent {
    color: #4CAF50;
    background: rgba(76, 175, 80, 0.1);
  }

  &.fortune-average {
    color: #FF9800;
    background: rgba(255, 152, 0, 0.1);
  }

  &.fortune-bad {
    color: #F44336;
    background: rgba(244, 67, 54, 0.1);
  }
}

/* ---------- 三才配置（移动端） ---------- */
.mobile-talents-section {
  margin-bottom: var(--mobile-lg);
}

.mobile-talents-card {
  background: var(--mobile-bg-card);
  border: 1px solid var(--mobile-border-color);
  border-radius: var(--mobile-radius-md);
  padding: var(--mobile-md);
  box-shadow: var(--mobile-shadow-card);
}

.mobile-talents-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--mobile-sm);
}

.mobile-talents-elements {
  display: flex;
  gap: var(--mobile-xs);
}

.mobile-talent-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--mobile-accent);
  color: #fff;
  font-weight: 700;
  font-size: var(--mobile-font-lg);
}

.mobile-talents-type {
  font-size: var(--mobile-font-sm);
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 10px;

  &.fortune-good,
  &.fortune-excellent {
    color: #4CAF50;
    background: rgba(76, 175, 80, 0.1);
  }

  &.fortune-average {
    color: #FF9800;
    background: rgba(255, 152, 0, 0.1);
  }

  &.fortune-bad {
    color: #F44336;
    background: rgba(244, 67, 54, 0.1);
  }
}

.mobile-talents-desc {
  font-size: var(--mobile-font-sm);
  color: var(--mobile-text-secondary);
  line-height: 1.6;
  margin: 0;
}

/* ---------- 姓名分析结果（移动端卡片列表） ---------- */
.mobile-results-section {
  margin-bottom: var(--mobile-lg);
}

.mobile-result-card {
  background: var(--mobile-bg-card);
  border: 1px solid var(--mobile-border-color);
  border-radius: var(--mobile-radius-md);
  padding: var(--mobile-md);
  box-shadow: var(--mobile-shadow-card);
}

.mobile-result-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--mobile-md);
  padding-bottom: var(--mobile-sm);
  border-bottom: 1px solid var(--mobile-border-color);
}

.mobile-result-name {
  font-size: var(--mobile-font-lg);
  font-weight: 600;
  color: var(--mobile-text-primary);
}

.mobile-result-score {
  font-size: var(--mobile-font-lg);
  font-weight: 700;
  padding: 3px 12px;
  border-radius: 10px;

  &.excellent {
    background: #4CAF50;
    color: #fff;
  }

  &.good {
    background: #2196F3;
    color: #fff;
  }

  &.average {
    background: #FF9800;
    color: #fff;
  }

  &.poor {
    background: #F44336;
    color: #fff;
  }
}

.mobile-result-grids {
  display: flex;
  flex-direction: row;
  overflow-x: auto;
  gap: 8px;
  padding-bottom: 4px;
  -webkit-overflow-scrolling: touch;
  margin-bottom: var(--mobile-md);
}

.mobile-result-grid-item {
  display: flex;
  flex-shrink: 0;
  min-width: 80px;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 8px;
  border-radius: var(--mobile-radius-sm);
  border-top: 3px solid var(--mobile-border-color);

  &.fortune-good {
    border-top-color: #4CAF50;
    background: rgba(76, 175, 80, 0.05);
  }

  &.fortune-average {
    border-top-color: #FF9800;
    background: rgba(255, 152, 0, 0.05);
  }

  &.fortune-bad {
    border-top-color: #F44336;
    background: rgba(244, 67, 54, 0.05);
  }
}

.mobile-result-grid-name {
  font-size: var(--mobile-font-sm);
  color: var(--mobile-text-secondary);
}

.mobile-result-grid-val {
  font-size: var(--mobile-font-lg);
  font-weight: 700;
  color: var(--mobile-accent);
}

.mobile-result-grid-type {
  font-size: 11px;
  font-weight: 600;
}

.mobile-suggestions {
  display: flex;
  flex-direction: column;
  gap: var(--mobile-xs);
}

.mobile-suggestion-item {
  font-size: var(--mobile-font-sm);
  color: var(--mobile-text-secondary);
  padding: var(--mobile-sm);
  background: var(--mobile-accent-light);
  border-radius: var(--mobile-radius-sm);
  line-height: 1.5;
  border-left: 3px solid var(--mobile-accent);
}
</style>
